import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getEngagementOverview, getEngagementTenants, getTenantTimeline, runRollup } from 'API/engagement';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEGMENTS, TENANT_STATES, type EngagementTenantRow, type Risk, type TenantState, type TimelineEntry } from 'types/engagement.types';
import { TrendChart } from '../../shared/charts';
import { fmtDate, fmtInt, fmtPct } from '../../shared/format';
import { MetricText } from '../../shared/metric';
import { KpiRow, KpiTile, QueryState, RiskPill, SectionCard, StatePill } from '../../shared/ui';

const TIMELINE_COLORS: Record<TimelineEntry['kind'], string> = {
  user: '#4F44FF',
  autopilot: '#1B7F3B',
  message: '#FFA502',
  incident: '#B42318',
};

const SEGMENT_COLORS: Record<string, string> = { heavy: '#4F44FF', normal: '#8C85FF', occasional: '#C9C5FF', inactive: '#E3E3E8' };

const SegmentBar: React.FC<{ segments?: EngagementTenantRow['segments']; users: number }> = ({ segments, users }) => {
  const { t } = useTranslation();
  if (!segments || users <= 0) return <span>—</span>;
  return (
    <Stack direction={'row'} sx={{ width: 90, height: 10, borderRadius: 1, overflow: 'hidden', bgcolor: '#F2F2F7' }}>
      {SEGMENTS.map((s) =>
        segments[s] ? (
          <Box key={s} title={`${t(`engSegment_${s}`)}: ${segments[s]}`} sx={{ width: `${((segments[s] ?? 0) / users) * 100}%`, bgcolor: SEGMENT_COLORS[s] }} />
        ) : null
      )}
    </Stack>
  );
};

export const TenantTimeline: React.FC<{ tenant: EngagementTenantRow; onClose(): void }> = ({ tenant, onClose }) => {
  const { t, i18n } = useTranslation();
  const query = useQuery({
    queryKey: ['engagement', 'timeline', tenant.tenantId],
    queryFn: () => getTenantTimeline(tenant.tenantId, 30),
  });
  return (
    <SectionCard
      title={t('engTimelineTitle', { name: tenant.name })}
      action={
        <Button size={'small'} onClick={onClose}>
          {t('engClose')}
        </Button>
      }
    >
      <Stack direction={'row'} gap={2} sx={{ mb: 1.5 }} flexWrap={'wrap'}>
        {(Object.keys(TIMELINE_COLORS) as TimelineEntry['kind'][]).map((k) => (
          <Stack key={k} direction={'row'} alignItems={'center'} gap={0.75}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TIMELINE_COLORS[k] }} />
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t(`engTimelineKind_${k}`)}</Typography>
          </Stack>
        ))}
      </Stack>
      <QueryState isLoading={query.isLoading} error={query.error} isEmpty={(query.data ?? []).length === 0} emptyText={t('engTimelineEmpty')}>
        <Stack gap={1}>
          {(query.data ?? []).map((e, i) => (
            <Stack key={`${e.at}-${i}`} direction={'row'} gap={1.5} alignItems={'baseline'}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TIMELINE_COLORS[e.kind] ?? '#999', flexShrink: 0, mt: '5px' }} />
              <Typography sx={{ fontSize: 12, color: 'text.secondary', width: 110, flexShrink: 0 }}>{fmtDate(e.at, i18n.language, true)}</Typography>
              <Typography sx={{ fontSize: 13 }}>
                {e.userName ? <strong>{e.userName}: </strong> : null}
                {e.text}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </QueryState>
    </SectionCard>
  );
};

const CustomersTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const lng = i18n.language;
  const [stateFilter, setStateFilter] = useState<TenantState | ''>('');
  const [riskFilter, setRiskFilter] = useState<Risk | ''>('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<EngagementTenantRow | null>(null);

  const overview = useQuery({ queryKey: ['engagement', 'overview', 28], queryFn: () => getEngagementOverview(28) });
  const tenants = useQuery({
    queryKey: ['engagement', 'tenants', stateFilter, riskFilter, q],
    queryFn: () => getEngagementTenants({ state: stateFilter, risk: riskFilter, q }),
  });
  const rollup = useMutation({
    mutationFn: runRollup,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['engagement'] }),
  });

  const o = overview.data;
  const k = o?.kpis;
  const diff = k ? k.activeUsers7d - k.activeUsersPrev7d : 0;
  // Days before the first measured day come as null (unknown, not zero) and are left out.
  const trend = (o?.trend ?? []).flatMap((p) => (p.activeUsers === null ? [] : [{ date: p.date, value: p.activeUsers }]));

  return (
    <Stack gap={2.5}>
      <QueryState isLoading={overview.isLoading} error={overview.error}>
        {o ? (
          <>
            {o.status && o.status !== 'ok' ? (
              <Alert
                severity={o.status === 'failed' ? 'error' : 'info'}
                action={
                  <Button size={'small'} onClick={() => rollup.mutate()} disabled={rollup.isPending}>
                    {t('engRollupRun')}
                  </Button>
                }
              >
                {t('engOverviewStatus', { status: t(`engMetricStatus_${o.status}`) })}
                {o.reason ? ` — ${o.reason}` : ''}
              </Alert>
            ) : null}
            {k ? (
              <KpiRow>
                <KpiTile
                  label={t('engKpiActiveUsers7d')}
                  value={fmtInt(lng)(k.activeUsers7d)}
                  sub={t('engKpiActiveUsersSub', { diff: `${diff >= 0 ? '+' : ''}${diff}`, total: k.totalUsers })}
                />
                <KpiTile label={t('engKpiStickiness')} value={<MetricText value={k.stickiness} format={fmtPct(lng)} />} sub={t('engKpiStickinessSub')} />
                <KpiTile label={t('engKpiAutopilot')} value={`${k.runningAutopilot} / ${k.tenants}`} sub={t('engKpiAutopilotSub')} />
                <KpiTile label={t('engKpiApprovals24h')} value={fmtInt(lng)(k.pendingApprovals24h)} tone={k.pendingApprovals24h > 0 ? 'warn' : 'default'} />
                <KpiTile label={t('engKpiReplies48h')} value={fmtInt(lng)(k.unansweredReplies48h)} tone={k.unansweredReplies48h > 0 ? 'warn' : 'default'} />
              </KpiRow>
            ) : null}
            <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
              <Box sx={{ flex: 2, minWidth: 0 }}>
                <SectionCard title={t('engTrendTitle')}>
                  {trend.length ? (
                    <TrendChart
                      ariaLabel={t('engTrendTitle')}
                      points={trend}
                      annotations={o.annotations}
                    />
                  ) : (
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('engTrendEmpty')}</Typography>
                  )}
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>{t('engTrendLegend')}</Typography>
                </SectionCard>
              </Box>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <SectionCard title={t('engStatesTitle')}>
                  <Stack gap={1}>
                    {TENANT_STATES.map((s) => (
                      <Stack
                        key={s}
                        direction={'row'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setStateFilter((prev) => (prev === s ? '' : s))}
                      >
                        <StatePill state={s} />
                        <Typography sx={{ fontWeight: 600 }}>{o.states?.[s] ?? 0}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                  {o.computedAt ? (
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 1.5 }}>
                      {t('engComputedAt', { at: fmtDate(o.computedAt, lng, true) })}
                    </Typography>
                  ) : null}
                </SectionCard>
              </Box>
            </Stack>
          </>
        ) : null}
      </QueryState>

      <SectionCard title={t('engTenantsTitle')}>
        <Stack direction={'row'} gap={1.5} sx={{ mb: 1.5 }} flexWrap={'wrap'}>
          <TextField size={'small'} label={t('engSearch')} value={q} onChange={(e) => setQ(e.target.value)} />
          <TextField
            select
            size={'small'}
            label={t('engFilterState')}
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as TenantState | '')}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value={''}>{t('engAll')}</MenuItem>
            {TENANT_STATES.map((s) => (
              <MenuItem key={s} value={s}>
                {t(`engState_${s}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size={'small'}
            label={t('engFilterRisk')}
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value as Risk | '')}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value={''}>{t('engAll')}</MenuItem>
            {(['stable', 'watch', 'at_risk'] as Risk[]).map((r) => (
              <MenuItem key={r} value={r}>
                {t(`engRisk_${r}`)}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction={'row'} gap={1} alignItems={'center'}>
            {SEGMENTS.map((s) => (
              <Chip key={s} size={'small'} label={t(`engSegment_${s}`)} sx={{ bgcolor: SEGMENT_COLORS[s], color: s === 'heavy' ? '#FFF' : '#1A1A2E' }} />
            ))}
          </Stack>
        </Stack>
        <QueryState isLoading={tenants.isLoading} error={tenants.error} isEmpty={(tenants.data ?? []).length === 0} emptyText={t('engTenantsEmpty')}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size={'small'}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('engColTenant')}</TableCell>
                  <TableCell align={'right'}>{t('engColUsers')}</TableCell>
                  <TableCell>{t('engColSegments')}</TableCell>
                  <TableCell>{t('engColAutopilot')}</TableCell>
                  <TableCell>{t('engColWaiting')}</TableCell>
                  <TableCell>{t('engColState')}</TableCell>
                  <TableCell>{t('engColRisk')}</TableCell>
                  <TableCell>{t('engColLastMessage')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(tenants.data ?? []).map((row) => (
                  <TableRow
                    key={row.tenantId}
                    hover
                    selected={selected?.tenantId === row.tenantId}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelected(row)}
                  >
                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{row.name}</Typography>
                      {row.product ? <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.product}</Typography> : null}
                    </TableCell>
                    <TableCell align={'right'}>{row.users}</TableCell>
                    <TableCell>
                      <SegmentBar segments={row.segments} users={row.users} />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13 }}>{t('engCampaignsCount', { count: row.runningCampaigns })}</Typography>
                      {row.accountStatus ? <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.accountStatusText ?? row.accountStatus}</Typography> : null}
                    </TableCell>
                    <TableCell>
                      {row.pendingApprovals24h || row.unansweredReplies48h ? (
                        <Typography sx={{ fontSize: 13 }}>
                          {row.pendingApprovals24h ? t('engApprovalsCount', { count: row.pendingApprovals24h }) : ''}
                          {row.pendingApprovals24h && row.unansweredReplies48h ? ' · ' : ''}
                          {row.unansweredReplies48h ? t('engRepliesCount', { count: row.unansweredReplies48h }) : ''}
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <StatePill state={row.state} />
                    </TableCell>
                    <TableCell>{row.risk ? <RiskPill risk={row.risk} /> : '—'}</TableCell>
                    <TableCell>{fmtDate(row.lastMessageAt, lng)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </QueryState>
      </SectionCard>

      {selected ? <TenantTimeline tenant={selected} onClose={() => setSelected(null)} /> : null}
    </Stack>
  );
};

export default CustomersTab;
